import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import './Blog.css';

function formatDate(str) {
  return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function renderInline(text) {
  const result = [];
  const boldRe = /^\*\*([^*]+)\*\*$/;
  const codeRe = /^`([^`]+)`$/;
  const combined = /(\[([^\]]+)\]\(([^)]+)\)|\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, m, k = 0;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) result.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    const v = m[0];
    const lm = v.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (lm) {
      result.push(<a key={k++} href={lm[2]} target="_blank" rel="noopener noreferrer" className="blog-post__link">{lm[1]}</a>);
    } else if (boldRe.test(v)) {
      result.push(<strong key={k++}>{v.slice(2,-2)}</strong>);
    } else if (codeRe.test(v)) {
      result.push(<code key={k++} className="blog-post__code">{v.slice(1,-1)}</code>);
    }
    last = m.index + v.length;
  }
  if (last < text.length) result.push(<span key={k++}>{text.slice(last)}</span>);
  return result;
}

function renderContent(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## '))  { elements.push(<h2 key={key++} className="blog-post__h2">{line.slice(3)}</h2>); i++; continue; }
    if (line.startsWith('# '))   { elements.push(<h1 key={key++} className="blog-post__h1">{line.slice(2)}</h1>); i++; continue; }
    if (line.startsWith('### ')) { elements.push(<h3 key={key++} className="blog-post__h3">{line.slice(4)}</h3>); i++; continue; }
    if (line.trim() === '') { i++; continue; }
    const paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#')) {
      paraLines.push(lines[i]); i++;
    }
    if (paraLines.length) {
      elements.push(<p key={key++} className="blog-post__p">{renderInline(paraLines.join(' '))}</p>);
    }
  }
  return elements;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allSlugs, setAllSlugs] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`/blog/${slug}.json?t=${Date.now()}`).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
      fetch(`/blog/index.json?t=${Date.now()}`).then(r => r.json()),
    ])
      .then(([postData, slugs]) => { setPost(postData); setAllSlugs(slugs); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="page" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <div className="blog-loading"><div className="blog-loading__spinner" /><span>Loading...</span></div>
    </div>
  );

  if (notFound || !post) return <Navigate to="/blog" replace />;

  // Build sorted list from index for prev/next
  const sorted = allSlugs.map(s => ({ slug: s }));
  const idx = sorted.findIndex(p => p.slug === slug);
  const prevSlug = sorted[idx + 1]?.slug || null;
  const nextSlug = sorted[idx - 1]?.slug || null;

  return (
    <div className="page blog-post-page">
      <div className="container">
        <div className="blog-post-layout">
          <article className="blog-post">
            <Link to="/blog" className="blog-post__back">← Back to Blog</Link>
            <header className="blog-post__header">
              <div className="blog-post__meta">
                <span className="blog-card__tag" style={{ background: `${post.tagColor}18`, color: post.tagColor }}>
                  {post.tag}
                </span>
                <span className="blog-card__date">{formatDate(post.date)}</span>
              </div>
              <h1 className="blog-post__title">{post.title}</h1>
              <p className="blog-post__excerpt">{post.excerpt}</p>
            </header>
            <hr className="blog-post__divider" />
            <div className="blog-post__content">{renderContent(post.content)}</div>
            <div className="blog-post__nav">
              {prevSlug ? (
                <Link to={`/blog/${prevSlug}`} className="blog-post__nav-link blog-post__nav-link--prev">
                  <span>← Previous</span>
                  <strong>{prevSlug.replace(/-/g, ' ')}</strong>
                </Link>
              ) : <div />}
              {nextSlug ? (
                <Link to={`/blog/${nextSlug}`} className="blog-post__nav-link blog-post__nav-link--next">
                  <span>Next →</span>
                  <strong>{nextSlug.replace(/-/g, ' ')}</strong>
                </Link>
              ) : <div />}
            </div>
          </article>

          <aside className="blog-post__sidebar">
            <div className="blog-post__sidebar-card">
              <h4>Other posts</h4>
              <div className="blog-post__sidebar-links">
                {allSlugs.filter(s => s !== slug).length === 0 && (
                  <p style={{ fontSize:'0.82rem', color:'var(--gray)' }}>No other posts yet.</p>
                )}
                {allSlugs.filter(s => s !== slug).map(s => (
                  <Link key={s} to={`/blog/${s}`} className="blog-post__sidebar-link">
                    <span>{s.replace(/-/g, ' ')}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div className="blog-post__sidebar-card">
              <h4>Stay updated</h4>
              <p>Join the Discord to get notified about new posts and product updates.</p>
              <a href="https://discord.gg/permsystems" target="_blank" rel="noopener noreferrer"
                 className="btn btn--dark btn--sm" style={{ marginTop:8, width:'100%', justifyContent:'center' }}>
                Join Discord
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}